// 세션 캐시 — `supabase.auth.getSession()`을 API 호출의 크리티컬 패스에서 걷어낸다.
//
// 배경 (2026-07-25 "오프라인 로딩 고착"):
//   `getSession()`은 내부적으로 auth 락을 통과한다. 그 락을 refresh 요청이 쥐고 매달리면
//   대기자는 타임아웃 없이 무한 대기한다(`_acquireLock`의 "이미 락 보유 중" 분기 —
//   자세한 경로는 [[timeoutFetch.ts]] 주석). apiFetch가 매 호출마다 이 함수를 await 했기에
//   화면 전체가 로딩에 고착됐다.
//
// 대응 두 겹:
//   ① [[timeoutFetch.ts]] — refresh 요청에 타임아웃을 걸어 좀비 락 자체를 없앤다(근본).
//   ② 이 파일 — 그래도 락이 막히면 캐시된 세션으로 진행한다(방어).
//      정상 상태에서는 `onAuthStateChange`가 밀어준 캐시를 그대로 쓰므로 락을 아예 건드리지 않는다.

import type { Session } from "@supabase/supabase-js";
import { Preferences } from "@capacitor/preferences";
import { supabase, AUTH_STORAGE_KEY } from "./supabase";

/** 락이 막혔다고 판단하기까지 기다리는 시간. 유저가 "멈췄다"고 느끼기 전에 끊어야 한다. */
export const SESSION_WAIT_TIMEOUT_MS = 4_000;
/** 만료 이 시간 전부터는 캐시를 신뢰하지 않고 라이브러리에 최신 세션을 다시 묻는다. */
const REFRESH_MARGIN_MS = 60_000;

let cachedSession: Session | null = null;
let subscribed = false;

/** onAuthStateChange 구독 1회 등록 — 로그인·로그아웃·TOKEN_REFRESHED가 캐시를 최신으로 유지한다. */
function ensureSubscribed(): void {
  if (subscribed) return;
  subscribed = true;
  // ⚠ 이 콜백은 auth 락 안에서 실행된다 → 대입만 하고 절대 await 하지 않는다(락 점유 연장 금지).
  supabase.auth.onAuthStateChange((_event, session) => {
    cachedSession = session;
  });
}

/** 캐시가 아직 유효한지 — 만료 여유(REFRESH_MARGIN_MS) 안쪽이면 갱신이 필요하다고 본다. */
function isFresh(session: Session | null, now: number): boolean {
  if (!session?.access_token) return false;
  const expiresAtMs = (session.expires_at ?? 0) * 1000;
  return expiresAtMs - now > REFRESH_MARGIN_MS;
}

/**
 * 저장소(Capacitor Preferences)에서 세션을 직접 읽는다 — 라이브러리가 막혔을 때의 최후 수단.
 * **만료 여부를 따지지 않는다**: 오프라인 부팅에서 세션을 null로 돌려주면 로그인 화면으로
 * 튕겨 "로그아웃된 것처럼 보이는" 더 나쁜 회귀가 생긴다. 로그아웃 시 라이브러리가 이 키를
 * 지우므로, 값이 남아 있다는 것 자체가 "로그인 상태였다"는 증거다.
 */
async function readStoredSession(): Promise<Session | null> {
  try {
    const { value } = await Preferences.get({ key: AUTH_STORAGE_KEY });
    if (!value) return null;
    const parsed = JSON.parse(value) as Session | null;
    // 세션 형태 최소 검증 (auth-js `_isValidSession`과 동일 기준)
    if (parsed && typeof parsed === "object" && "access_token" in parsed && "refresh_token" in parsed) {
      return parsed;
    }
    return null;
  } catch {
    // 저장소 접근 실패·JSON 깨짐 — 캐시 없음으로 취급
    return null;
  }
}

/** 타임아웃이 붙은 await. 시간 초과·throw를 모두 "못 얻었다"로 동일 취급한다. */
async function raceWithTimeout<T>(promise: Promise<T>, ms: number): Promise<{ ok: true; value: T } | { ok: false }> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const timeout = new Promise<{ ok: false }>((resolve) => {
      timer = setTimeout(() => resolve({ ok: false }), ms);
    });
    return await Promise.race([promise.then((value) => ({ ok: true as const, value })), timeout]);
  } catch {
    return { ok: false };
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/**
 * 현재 세션을 얻는다. **절대 무한 대기하지 않는다.**
 * @returns degraded=true 는 "신뢰할 수 있는 최신 세션을 못 얻어 캐시/저장소 값으로 대체했다"는 뜻
 *          (오프라인 또는 auth 락 정체). 호출자는 그래도 진행해야 한다 — 실패는 fetch가 판정한다.
 */
export async function getSessionSafe(
  timeoutMs: number = SESSION_WAIT_TIMEOUT_MS,
): Promise<{ session: Session | null; degraded: boolean }> {
  ensureSubscribed();
  const now = Date.now();
  // 캐시가 충분히 신선하면 락을 건드리지 않고 즉시 반환 (정상 경로 — 고착 원인 자체를 우회)
  if (isFresh(cachedSession, now)) return { session: cachedSession, degraded: false };

  const raced = await raceWithTimeout(supabase.auth.getSession(), timeoutMs);
  if (raced.ok) {
    cachedSession = raced.value.data.session;
    return { session: cachedSession, degraded: false };
  }

  // 라이브러리가 시간 안에 답하지 못했다 → 캐시, 없으면 저장소에서 복구
  if (!cachedSession) cachedSession = await readStoredSession();
  return { session: cachedSession, degraded: true };
}

/** Authorization 헤더에 실을 access token. 세션 확인이 막혀도 유한 시간에 반환된다. */
export async function getAccessTokenSafe(
  timeoutMs: number = SESSION_WAIT_TIMEOUT_MS,
): Promise<{ token: string | null; degraded: boolean }> {
  const { session, degraded } = await getSessionSafe(timeoutMs);
  return { token: session?.access_token ?? null, degraded };
}

/** 테스트 전용 — 모듈 캐시 초기화. */
export function __resetAuthSessionCacheForTest(): void {
  cachedSession = null;
  subscribed = false;
}
